import { useState } from 'react';
import type { ClaimInput, Tenant } from '../../../identity/api';
import { Chip } from '@nyce/admin-kit/ui';
import { client } from '../../../identity/session';
import { ClaimPicker } from './ClaimPicker';
import { ConfirmButton, EmptyState, ErrorBox, TableScroller } from '../../../components/common';

/**
 * `tenant_claims`: `scope` rows become the space-separated `scope` claim, `role` rows the plural `roles` array.
 * These are the authority source of every token the tenant issues — identity providers cannot inject them.
 */
export function ClaimsTab({
  tenant,
  canWrite,
  onChanged,
  notify,
}: {
  tenant: Tenant;
  canWrite: boolean;
  onChanged: () => Promise<void>;
  notify: { success: (t: string) => void; error: (t: string) => void };
}) {
  const [error, setError] = useState<string | null>(null);

  const add = async (cs: ClaimInput[]) => {
    setError(null);
    const done: string[] = [];
    try {
      for (const c of cs) {
        await client.tenants.claims.add(tenant.id, c);
        done.push(`${c.type} ${c.value}`);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      if (done.length) {
        await onChanged();
        notify.success(`Added ${done.join(', ')}`);
      }
    }
  };
  const remove = async (id: string, label: string) => {
    try {
      await client.tenants.claims.remove(tenant.id, id);
      await onChanged();
      notify.success(`Removed ${label}`);
    } catch (err) {
      notify.error((err as Error).message);
    }
  };

  const claims = [...tenant.claims].sort(
    (a, b) => a.type.localeCompare(b.type) || a.value.localeCompare(b.value)
  );
  return (
    <>
      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Claims</h2>
          <span className="nyk-ui-hint">
            scope → <code>scope</code> claim (wildcards like <code>service-a:*</code>), role →{' '}
            <code>roles</code> claim
          </span>
        </div>
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" data-testid="claims-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Value</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => (
                <tr key={c.id} data-claim={c.id}>
                  <td data-label="Type">
                    <Chip size="sm" selected={c.type === 'scope'}>
                      {c.type}
                    </Chip>
                  </td>
                  <td data-label="Value" className="nyk-card-title nyk-wrap">
                    <code>{c.value}</code>
                  </td>
                  <td className="nyk-row-actions">
                    {canWrite && (
                      <ConfirmButton
                        size="sm"
                        variant="danger"
                        message={`Remove ${c.type} ${c.value}? Tokens lose it at their next refresh.`}
                        onConfirm={() => void remove(c.id, `${c.type} ${c.value}`)}
                      >
                        Remove
                      </ConfirmButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
        {!claims.length && (
          <EmptyState
            title="No claims."
            hint="Tokens of this tenant carry an empty scope: every downstream service answers 403 (default deny)."
          />
        )}
      </div>
      {canWrite && (
        <div className="nyk-card" data-testid="claim-form">
          <div className="nyk-card-head">
            <h2>Add claims</h2>
            <span className="nyk-ui-hint">Click a known claim or a preset, or type a custom one.</span>
          </div>
          <ErrorBox error={error} />
          <ClaimPicker value={tenant.claims} onAdd={add} />
          <p className="nyk-card-intro" style={{ marginTop: 12, marginBottom: 0 }}>
            Scopes follow <code>&lt;service&gt;:&lt;resource&gt;:&lt;action&gt;</code>;{' '}
            <code>auth:tenants:read</code> / <code>auth:tenants:write</code> grant this console.
          </p>
        </div>
      )}
    </>
  );
}
